import type { StaticPreviewRow } from '../designer/types/form.types'

export type JournalLineSource = {
  line_number?: number
  account_id?: string | null
  account_display?: string | null
  debit_amount?: number | null
  credit_amount?: number | null
  description?: string | null
}

/** Register movement row from `/movements` (accumulation register balances). */
export type MovementLineSource = {
  account_id?: string | null
  debit_total?: number | null
  credit_total?: number | null
  line_number?: number
}

export function formatJournalAmount(value: number | null | undefined): string {
  const n = Number(value ?? 0)
  if (!Number.isFinite(n) || n === 0) return '—'
  return new Intl.NumberFormat('fr-CA', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })
    .format(n)
    .replace(/\u00a0/g, ' ')
}

function accountLabel(
  accountId: string | null | undefined,
  displayById: Map<string, string>,
): string {
  if (!accountId) return '—'
  return displayById.get(accountId) || accountId
}

/** Document tabular `Lines` → journal-preview staticPreviewRows (+ balance footer). */
export function linesToJournalPreviewRows(
  lines: JournalLineSource[],
  displayById: Map<string, string>,
): StaticPreviewRow[] {
  if (!lines.length) return []

  let totalDebit = 0
  let totalCredit = 0
  const body: StaticPreviewRow[] = []

  lines.forEach((line, idx) => {
    const debit = Number(line.debit_amount ?? 0)
    const credit = Number(line.credit_amount ?? 0)
    if (debit === 0 && credit === 0) return
    totalDebit += debit
    totalCredit += credit
    const num = line.line_number ?? idx + 1
    body.push({
      num: String(num),
      account:
        line.account_display ||
        accountLabel(line.account_id, displayById),
      debit: formatJournalAmount(debit),
      credit: formatJournalAmount(credit),
    })
  })

  if (!body.length) return []

  const balanced = Math.abs(totalDebit - totalCredit) < 0.005
  body.push({
    num: '',
    account: balanced ? '✓ Équilibre vérifié' : '⚠ Déséquilibre',
    debit: formatJournalAmount(totalDebit),
    credit: formatJournalAmount(totalCredit),
    __total: true,
  })
  return body
}

/** Posted register movements → same journal row shape as Lines. */
export function movementsToJournalPreviewRows(
  movements: MovementLineSource[],
  displayById: Map<string, string>,
): StaticPreviewRow[] {
  const lines: JournalLineSource[] = movements.map((m, idx) => ({
    line_number: m.line_number ?? idx + 1,
    account_id: m.account_id,
    debit_amount: Number(m.debit_total ?? 0),
    credit_amount: Number(m.credit_total ?? 0),
  }))
  return linesToJournalPreviewRows(lines, displayById)
}

export function collectAccountIds(
  lines: JournalLineSource[],
  movements: MovementLineSource[],
): string[] {
  const ids = new Set<string>()
  for (const l of lines) {
    if (l.account_id) ids.add(String(l.account_id))
  }
  for (const m of movements) {
    if (m.account_id) ids.add(String(m.account_id))
  }
  return Array.from(ids)
}

/** Map preview table nodes to row sets (first table = primary journal). */
export function assignRowsToJournalTables(
  tableNodes: { name: string }[],
  rowSets: StaticPreviewRow[][],
): Record<string, StaticPreviewRow[]> {
  const out: Record<string, StaticPreviewRow[]> = {}
  tableNodes.forEach((t, i) => {
    out[t.name] = rowSets[i] ?? []
  })
  return out
}

export function movementRowsToLineSources(movements: MovementLineSource[]): JournalLineSource[] {
  return movements.map((m, idx) => ({
    line_number: m.line_number ?? idx + 1,
    account_id: m.account_id,
    debit_amount: Number(m.debit_total ?? 0),
    credit_amount: Number(m.credit_total ?? 0),
  }))
}

/**
 * Lines for journal UI: saved tabular → posted movements → client draft (never schema mock).
 * When post wrote only écriture A (2 rows), append draft lines 3–4 for écriture B preview.
 */
export function resolveJournalLineSources(
  lines: JournalLineSource[],
  movements: MovementLineSource[],
  hasPostedMovements: boolean,
  draftLines: JournalLineSource[],
): JournalLineSource[] {
  if (lines.length) return lines
  const draft = draftLines.length ? draftLines : []
  if (hasPostedMovements && movements.length) {
    const fromMov = movementRowsToLineSources(movements)
    if (fromMov.length >= 4) return fromMov
    const tail = draft.filter((l) => Number(l.line_number ?? 0) > fromMov.length)
    return tail.length ? [...fromMov, ...tail] : fromMov
  }
  return draft
}

/** Split Lines/movements into écriture A (lines 1–2) and B (3–4) for JournalPreview tables. */
export function splitLinesForJournalEcritures(
  lines: JournalLineSource[],
  movements: MovementLineSource[],
  useMovements: boolean,
): [JournalLineSource[], JournalLineSource[]] {
  const source: JournalLineSource[] = useMovements
    ? movements.map((m, idx) => ({
        line_number: m.line_number ?? idx + 1,
        account_id: m.account_id,
        debit_amount: Number(m.debit_total ?? 0),
        credit_amount: Number(m.credit_total ?? 0),
      }))
    : lines

  const sorted = [...source].sort(
    (a, b) => Number(a.line_number ?? 0) - Number(b.line_number ?? 0),
  )

  if (sorted.length <= 2) {
    return [sorted, []]
  }

  const ecritureA = sorted.filter((l) => {
    const n = Number(l.line_number ?? 0)
    return n <= 0 || n <= 2
  })
  const ecritureB = sorted.filter((l) => Number(l.line_number ?? 0) >= 3)
  return [ecritureA.length ? ecritureA : sorted.slice(0, 2), ecritureB]
}

export function ecritureLinesToPreviewRows(
  lines: JournalLineSource[],
  displayById: Map<string, string>,
): StaticPreviewRow[] {
  return linesToJournalPreviewRows(lines, displayById)
}
